import { call, delay, put, race, select, take } from 'redux-saga/effects';

import * as AudioActions from '../actions';
import * as AudioConstants from '../constants';
import * as D from '../deps';
import loadHarnesses, {
  getHarnessConfig,
  getPlayer,
  initializeAudioPlayback,
} from '../libs/AudioPlayback/AudioLoader';
import * as AudioUtils from '../utils';

const log = D.Logger.child({ component: 'sagas/workers/AudioPlayer' });

/* keep track of any immediate audio loading errors locally. */
const { localErrorCountExceeded, incrementLocalErrorCount, resetLocalErrorCount } = (() => {
  let localAudioErrorCount = 0;
  return {
    localErrorCountExceeded: () => localAudioErrorCount > AudioConstants.MAX_AUDIO_RETRIES,
    incrementLocalErrorCount: () => ++localAudioErrorCount,
    resetLocalErrorCount: () => {
      localAudioErrorCount = 0;
    },
  };
})();

// Only unbind events after PAUSED event is received or a brief timeout
function* unbindProvidersOnPaused(provider, events) {
  yield race({
    paused: take(D.Constants.AudioPlayer.AudioPlayerActions.PAUSED),
    delayed: delay(1),
  });
  yield call(provider.unbindEvents, events);
}

export function* bindProvider({ provider, providerEvents }) {
  const providerState = provider.getState();
  yield call(AudioUtils.setProviderEvents, providerState.src, Object.keys(providerEvents));
  yield call(provider.bindEvents, providerEvents);
}

export function* unbindProvider(provider) {
  if (provider) {
    const providerEvents = AudioUtils.getProviderEvents(provider.getState().src);
    if (providerEvents) {
      yield call(unbindProvidersOnPaused, provider, providerEvents);
    }
  }
}

export function* startPlaying(nextProvider, isMutedSelect, time, volume, gain) {
  yield put(AudioActions.logStartPlaying());
  yield put(AudioActions.activeProvider(nextProvider));
  // WEBCLIENT-3927 : added so we don't call setCurrentTime on the underlying element
  // if it is just starting.
  const adjTime = time === nextProvider.getState().currentTime && !time ? undefined : time;
  yield call(nextProvider.play, volume, gain, isMutedSelect, adjTime);
}

export function* pauseProvider(provider) {
  yield call(provider.pause);
}

export function* preloadTrackWorker({ payload: { track } }) {
  const player = getPlayer();
  const audioSrc = AudioUtils.getAudioSrcObj(track);

  if (audioSrc.key) {
    yield call(
      player.preload,
      audioSrc,
      AudioUtils.getDefaultAudioErrorHandler(audioSrc.url, audioSrc.token),
    );
  }
}

export function* playSource(audioSrc, time, gain = 0) {
  let nextProvider;
  let isCurrentlyMuted;
  let volume;

  const defaultError = AudioUtils.getDefaultAudioErrorHandler(audioSrc.url, audioSrc.token);
  try {
    const player = getPlayer();
    const activeTrackUrl = yield select(AudioUtils.getActiveTrackUrl);
    let prevProvider;

    // We need to try to get the previous provider before we create the next one in the cases
    // where the urls (audioSrc and activeTrackUrl) are the same, but a provider for the url
    // hasn't been created yet. An example of this is when the app is paused and inactive for
    // a period of time and the auth token expires. After re-auth we re-initialize AudioPlayback,
    // clearing out any existing providers. Under circumstances like this, if we don't get
    // prevProvider first, prevProvider === nextProvider and the unbindRebindProviders call
    // below will not run, breaking various parts of the app.
    if (activeTrackUrl) {
      try {
        prevProvider = yield call(player.get, activeTrackUrl);
      } catch (e) {
        /* Do nothing, this is expected */
      }
    }

    const isAudioPlayingSelect = yield select(AudioUtils.isAudioPlayerPlaying);
    const isSamplePlayingSelect = yield select(AudioUtils.isAuxPlaybackPlaying);
    const isPlaybackActive = isAudioPlayingSelect || isSamplePlayingSelect;
    volume = yield select(D.Utils.Volume.getGlobalVolume);
    isCurrentlyMuted = yield select(D.Utils.Volume.getGlobalMuted);

    if (prevProvider && activeTrackUrl === audioSrc.url && isPlaybackActive) {
      if (prevProvider.getState().paused) {
        yield call(startPlaying, prevProvider, isCurrentlyMuted, time, volume, gain);
      }
      return prevProvider;
    }

    yield put(D.Actions.AudioPlayer.setActiveTrackUrl(audioSrc.url));

    if (prevProvider) {
      yield call(pauseProvider, prevProvider);
      yield call(unbindProvider, prevProvider);
    }

    nextProvider = yield call(player.load, audioSrc, defaultError);

    /* when a track finishes playing we need to clear the provider. Need to carry the URL forward for this to properly happen */
    const additionalProviderData = {
      url: audioSrc.url,
      token: audioSrc.token,
      harnessName: nextProvider.getHarnessName(),
    };

    const providerEvents = yield call(
      AudioUtils.assignProviderEvents,
      audioSrc.audioType,
      D.getStore().dispatch,
      additionalProviderData,
    );
    yield call(bindProvider, { provider: nextProvider, providerEvents });
    yield call(startPlaying, nextProvider, isCurrentlyMuted, time, volume, gain);
    // Return this provider so up the chain we can clear it and pause it
    return nextProvider;
  } catch (e) {
    const error = `playSource failed with exception: ${JSON.stringify(e)}`;
    log.error(error);
    defaultError({
      message: JSON.stringify({
        commonType: AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
        message: error,
      }),
    });
  }
}

export function* playTrackWorker({ payload, payload: { track } }) {
  const currentTime = payload[AudioConstants.AudioPlayerKeys.CURRENT_TIME];

  let error;
  if (!track) {
    error =
      'AudioPlayer saga worker#playTrackWorker: expected parameter track of type immutable map';
  } else if (!track.get('audioURL') || !track.get('audioEncoding')) {
    error =
      'AudioPlayer saga worker#playTrackWorker: expected a track with audioUrl and audioEncoding parameter';
  }

  if (error) {
    log.error(error);
    const url = track?.get('audioURL');
    const token = track?.get('trackToken');
    AudioUtils.getDefaultAudioErrorHandler(
      url,
      token,
    )({
      message: JSON.stringify({
        commonType: AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
        message: error,
      }),
    });
    return;
  }

  const gain = yield call(AudioUtils.getGainFromTrack, track);
  const audioSrc = AudioUtils.getAudioSrcObj(track);

  const provider = yield call(playSource, audioSrc, currentTime, gain);
  const result = yield race({
    pause: take(D.Constants.AudioPlayer.AudioPlayerActions.PAUSE),
    ended: take(D.Constants.AudioPlayer.AudioPlayerActions.ENDED),
  });
  if (result.pause && provider) {
    yield call(pauseProvider, provider);
  }
  if (result.ended) {
    yield put(D.Actions.AudioPlayer.setActiveTrackUrl(''));
  }
}

export function* getInitializeAudioPlaybackOptions(options) {
  const tryForceFlash = yield select(D.Utils.TunerVars.getForceFlash);
  const audioData = {};
  audioData.isSingleHarnessForStationAndAudioAdsEnabled = yield select(
    D.Utils.Listener.isSingleHarnessForStationAndAudioAdsEnabled,
  );
  audioData.isPodcastsOnSxmPlayerEnabled = yield select(
    D.Utils.Listener.isPodcastsOnSxmPlayerEnabled,
  );
  audioData.isSxmUnencryptedMediaelementPlayback2021Enabled = yield select(
    D.Utils.Listener.isSxmUnencryptedMediaelementPlayback2021Enabled,
  );
  audioData.isSxmUnencryptedWAAPlayback2021Enabled = yield select(
    D.Utils.Listener.isSxmUnencryptedWAAPlayback2021Enabled,
  );
  audioData.isSxmFetchDownloaderEnabled = yield select(
    D.Utils.Listener.isSxmFetchDownloaderEnabled,
  );
  audioData.isSxmEncryptedStationFetchDownloaderEnabled = yield select(
    D.Utils.Listener.isSxmEncryptedStationFetchDownloaderEnabled,
  );
  audioData.isSxmFetchDownloaderV2Enabled = yield select(
    D.Utils.Listener.isSxmFetchDownloaderV2Enabled,
  );
  audioData.isSxmEncryptedStationFetchDownloaderV2Enabled = yield select(
    D.Utils.Listener.isSxmEncryptedStationFetchDownloaderV2Enabled,
  );
  audioData.isSxmEncryptedStationFragmentedEnabled = yield select(
    D.Utils.Listener.isSxmEncryptedStationFragmentedEnabled,
  );
  audioData.isSxmEncryptedStationFetchDownloaderV2MultipleHarnessEnabled = yield select(
    D.Utils.Listener.isSxmEncryptedStationFetchDownloaderV2MultipleHarnessEnabled,
  );

  const initOptions = Object.assign({ audioData }, options);

  if (tryForceFlash) {
    initOptions.tryForceFlash = tryForceFlash;
  }

  return initOptions;
}

export function* initializeAudio(options, isForced) {
  try {
    log.debug('initializeAudio(): attempting to initialize audio');
    const harnessConfig = yield call(getHarnessConfig);
    const harnessRequests = yield call(loadHarnesses, harnessConfig);
    const audioHarnesses = yield call(D.Utils.Core.promiseAll, harnessRequests);
    const audioPlayerSupportedBrowsers = yield select(
      D.Utils.TunerVars.getAudioPlayerSupportedBrowsers,
    );
    const playbackOptions = yield call(getInitializeAudioPlaybackOptions, options);

    yield call(
      initializeAudioPlayback,
      audioHarnesses,
      harnessConfig,
      audioPlayerSupportedBrowsers,
      playbackOptions,
      isForced,
    );

    yield put({
      type: D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_INITIALIZED,
    });
    resetLocalErrorCount();
  } catch (err) {
    incrementLocalErrorCount();
    const hasExceededMaxErrors = yield select(AudioUtils.hasAudioInitCountExceededMax);
    if (!hasExceededMaxErrors && !localErrorCountExceeded()) {
      yield call(initializeAudio, options, true);
    } else {
      yield put(
        D.Actions.UseCases.openErrorToast({
          ...AudioConstants.AudioMessages.havingTroubleStartingPlayback,
          messageName: D.Constants.Analytics.UserMessages.PLAYBACK_FAILURE,
        }),
      );
    }
    log.error('Failed to initialize audio');
  }
}

export function* onSetCurrentTime({ payload }) {
  const currentTime = payload[AudioConstants.AudioPlayerKeys.CURRENT_TIME];
  const track = yield select(AudioUtils.getActivelyPlayingTrack);
  const url = track.get('audioURL');
  const player = getPlayer();
  const provider = yield call(player.get, url);
  return yield call(provider.setCurrentTime, currentTime);
}

export function* onSetVolume({ payload: { volume } }) {
  const isMuted = yield select(D.Utils.Volume.getGlobalMuted);
  const isAuxPlaying = yield select(AudioUtils.isAuxPlaybackPlaying);
  let gain;
  let url;

  if (!isAuxPlaying) {
    const track = yield select(AudioUtils.getActivelyPlayingTrack);
    gain = track.get('fileGain');
    url = track.get('audioURL');
  } else {
    gain = AudioConstants.AUX_GAIN_DEFAULT;
    url = yield select(AudioUtils.getAuxSourceUrl);
  }

  if (isMuted && volume > 0) {
    yield put(D.Actions.AudioPlayer.unmuteAudio());
  } else if (volume === 0 && !isMuted) {
    yield put(D.Actions.AudioPlayer.muteAudio());
  }

  yield call(D.thunkify, D.Actions.Volume.setGlobalVolume, volume);

  const player = getPlayer();
  const provider = yield call(player.get, url);
  return yield call(provider.setVolume, volume, parseFloat(gain));
}

export function* onMute() {
  let url;
  const isAuxPlaying = yield select(AudioUtils.isAuxPlaybackPlaying);
  if (!isAuxPlaying) {
    const track = yield select(AudioUtils.getActivelyPlayingTrack);
    url = track.get('audioURL');
  } else {
    url = yield select(AudioUtils.getAuxSourceUrl);
  }

  yield put(D.Actions.Volume.setGlobalMuted(true));
  const player = getPlayer();
  const provider = yield call(player.get, url);
  return yield call(provider.setMuted, true);
}

export function* onUnmute() {
  let url;
  const isAuxPlaying = yield select(AudioUtils.isAuxPlaybackPlaying);
  if (!isAuxPlaying) {
    const track = yield select(AudioUtils.getActivelyPlayingTrack);
    url = track.get('audioURL');
  } else {
    url = yield select(AudioUtils.getAuxSourceUrl);
  }

  const volume = yield select(D.Utils.Volume.getGlobalVolume);
  yield put(D.Actions.Volume.setGlobalMuted(false));
  if (volume === 0) {
    yield put(
      D.Actions.AudioPlayer.setVolumeAudio(AudioConstants.VolumeConstants.DEFAULT_UNMUTE_VOLUME),
    );
  }
  const player = getPlayer();
  const provider = yield call(player.get, url);
  return yield call(provider.setMuted, false);
}

export function* reloadAudioPlaybackLibrary(action) {
  yield D.tryCall(initializeAudio, action.data, true);
  // Need to clear any previously playing track url from audio player state so
  // we will be sure to bind all events later in the Audio player workers
  yield put(D.Actions.AudioPlayer.setActiveTrackUrl(null));
  const trackToken = yield select(AudioUtils.getCurrentTrackToken);
  const trackTime = yield select(AudioUtils.getCurrentTrackTime);
  const trackToPlay = yield select(D.Utils.Entity.getTrackById, trackToken);
  yield put(
    D.Actions.Playback.intentPlayTrack({
      track: trackToPlay,
      seekToInSeconds: trackTime,
    }),
  );
}

export function* disableWakeLock() {
  const shouldUseLock = yield select(AudioUtils.shouldUseWakeLock);
  if (shouldUseLock) {
    yield put({
      type: D.Constants.AudioPlayer.AudioPlayerActions.SET_WAKE_LOCK,
      isOn: false,
    });
  }
}

export function* onPaused() {
  // This exists so actions that call pause on audio via putAndWait
  // will wait until the pause finishes; since pause is actually handled
  // as just canceling the play worker
  const wasPlaying = yield select(AudioUtils.isAudioPlayerPlaying);
  if (wasPlaying) {
    // wait for the paused action to come through or just timeout
    const result = yield race({
      paused: take(D.Constants.AudioPlayer.AudioPlayerActions.PAUSED),
      delayed: delay(1000),
    });
    if (result.delayed) {
      log.warn('Pause called, but no paused action resulted');
    }
  }
}

function* seekAudio(value) {
  const track = yield select(AudioUtils.getActivelyPlayingTrack);
  const url = track.get('audioURL');
  const duration = yield select(AudioUtils.getCurrentTrackDuration);

  const player = getPlayer();
  const provider = yield call(player.get, url);
  return yield call(provider.setCurrentTime, Math.max(Math.min(duration, value), 0));
}

export function* scrubAudio({ payload }) {
  yield D.tryCall(seekAudio, payload.value);
}

export function* handlePostUrlUpdate({ payload: { newUrl, track } }) {
  const audioSrc = AudioUtils.getAudioSrcObj(track);
  yield put(D.Actions.AudioPlayer.setActiveTrackUrl(newUrl));
  try {
    const player = getPlayer();
    const provider = yield call(player.get, newUrl);
    yield call(unbindProvider, provider);

    const additionalProviderData = {
      url: audioSrc.url,
      token: audioSrc.token,
      harnessName: provider.getHarnessName(),
    };

    const providerEvents = yield call(
      AudioUtils.assignProviderEvents,
      audioSrc.audioType,
      D.getStore().dispatch,
      additionalProviderData,
    );
    yield call(bindProvider, { provider, providerEvents });
  } catch (e) {
    log.error(`handlePostUrlUpdate failed with exception: ${JSON.stringify(e)}`);
  }
}
